import {useMemo, useState, type CSSProperties} from "react";
import {Button} from "@/primitives/Button";
import {FA6Icon} from "@/primitives/FA6Icon";
import {Field} from "@/primitives/Field";
import {Modal} from "@/primitives/Modal";
import {Select} from "@/primitives/Select";
import {useDialogState} from "@/shared/useDialogState";
import type {CsrfConfig, ExternalAuthConnection} from "../userSettingsPage";

type ProviderId = "google" | "azure";

interface ExternalAuthPanelProps {
	googleLoginAvailable: boolean;
	microsoftLoginAvailable: boolean;
	externalAuthOnly: boolean;
	connections: Partial<Record<ProviderId, ExternalAuthConnection>>;
	hasConnectedMobileDevices: boolean;
	csrf?: CsrfConfig;
}

const PROVIDER_LOGO_STYLE: CSSProperties = {width: 18, height: 18, float: "left", marginRight: 10};

const PROVIDER_NAMES: Record<ProviderId, string> = {google: "Google", azure: "Microsoft"};

/**
 * Full-page form POST so the server's redirect back to the settings page (and the
 * disconnectProviderNotAllowed flash attribute, TIMR-6449) behaves as a regular
 * Post/Redirect/Get. The endpoint is POST-only (TIMR-6189).
 */
function submitDisconnect(provider: ProviderId, disconnectMobileDevices: boolean, csrf?: CsrfConfig) {
	const form = document.createElement("form");
	form.method = "post";
	form.action = `/timr/user/disconnect/${provider}.html`;

	const parameter = document.createElement("input");
	parameter.type = "hidden";
	parameter.name = "disconnectMobileDevices";
	parameter.value = String(disconnectMobileDevices);
	form.appendChild(parameter);

	if (csrf) {
		const csrfInput = document.createElement("input");
		csrfInput.type = "hidden";
		csrfInput.name = csrf.parameterName;
		csrfInput.value = csrf.token;
		form.appendChild(csrfInput);
	}

	document.body.appendChild(form);
	form.submit();
}

export function ExternalAuthPanel({
	googleLoginAvailable,
	microsoftLoginAvailable,
	externalAuthOnly,
	connections,
	hasConnectedMobileDevices,
	csrf,
}: ExternalAuthPanelProps) {
	const dialog = useDialogState<ProviderId>();

	const disconnect = (provider: ProviderId) => {
		if (hasConnectedMobileDevices) {
			dialog.show(provider);
		} else {
			submitDisconnect(provider, false, csrf);
		}
	};

	return (
		<div className="panel panel-flat">
			<div className="panel-heading">
				<h5 className="panel-title">{messages["user.settings.connections"]}</h5>
			</div>
			<div className="panel-body form-horizontal">
				{googleLoginAvailable && (
					<ProviderRow
						name={PROVIDER_NAMES.google}
						logo="/timr/images/google-logo.svg"
						connectUrl="/timr/user/connect/google.html"
						connection={connections.google}
						externalAuthOnly={externalAuthOnly}
						onDisconnect={() => disconnect("google")}
					/>
				)}
				{microsoftLoginAvailable && (
					<ProviderRow
						name={PROVIDER_NAMES.azure}
						logo="/timr/images/azure-logo.png"
						connectUrl="/timr/user/connect/azure.html"
						connection={connections.azure}
						externalAuthOnly={externalAuthOnly}
						onDisconnect={() => disconnect("azure")}
					/>
				)}
				<Modal.Root open={dialog.open} onOpenChange={dialog.onOpenChange} disablePointerDismissal>
					<DisconnectDevicesModal
						key={dialog.key}
						providerName={dialog.value != null ? PROVIDER_NAMES[dialog.value] : ""}
						onConfirm={(disconnectMobileDevices) => {
							if (dialog.value != null) {
								submitDisconnect(dialog.value, disconnectMobileDevices, csrf);
							}
						}}
					/>
				</Modal.Root>
			</div>
		</div>
	);
}

function ProviderRow({
	name,
	logo,
	connectUrl,
	connection,
	externalAuthOnly,
	onDisconnect,
}: {
	name: string;
	logo: string;
	connectUrl: string;
	connection?: ExternalAuthConnection;
	externalAuthOnly: boolean;
	onDisconnect: () => void;
}) {
	return (
		<div className="form-group">
			<div className="col-sm-3 control-label text-left">
				<img src={logo} style={PROVIDER_LOGO_STYLE} alt=""/>
				{name}
			</div>
			<div className="col-sm-6 control-label text-left">
				{connection != null &&
					`${messages.withArguments("user.settings.connections.connectedAs", connection.connectedAs ?? "")}, ${connection.connectedAt}`}
			</div>
			<div className="col-sm-3">
				{connection != null ? (
					<Button
						variant="destructive"
						className="btn-block"
						disabled={externalAuthOnly}
						onClick={onDisconnect}
					>
						<FA6Icon icon="link-slash" variant="regular" fixedWidth className="mr-5"/>
						{messages["user.settings.connections.disconnect"]}
					</Button>
				) : (
					<Button
						variant="primary"
						className="btn-block"
						render={<a href={connectUrl}/>}
					>
						<FA6Icon icon="link" variant="regular" fixedWidth className="mr-5"/>
						{messages["user.settings.connections.connect"]}
					</Button>
				)}
			</div>
		</div>
	);
}

function DisconnectDevicesModal({
	providerName,
	onConfirm,
}: {
	providerName: string;
	onConfirm: (disconnectMobileDevices: boolean) => void;
}) {
	const [value, setValue] = useState<boolean | null>(null);
	const [showError, setShowError] = useState(false);

	const items = useMemo(
		() => [
			{label: messages["dialog.confirm.disconnectDevices.default"], value: null},
			{label: messages["dialog.confirm.disconnectDevices.yes"], value: true},
			{label: messages["dialog.confirm.disconnectDevices.no"], value: false},
		],
		[],
	);

	const confirm = () => {
		if (value == null) {
			setShowError(true);
			return;
		}
		onConfirm(value);
	};

	return (
		<Modal.Content>
			<Modal.Header>
				<Modal.Title>
					{messages.withArguments("user.settings.connections.disconnectProvider", providerName)}
				</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<p className="content-group">
					{messages.withArguments("user.settings.connections.disconnectProvider.info", providerName)}
				</p>
				<Field.Root name="disconnectMobileDevices" invalid={showError}>
					<Field.Label>
						{messages["dialog.confirm.disconnectDevices.label"]}
					</Field.Label>
					<Select.Root
						items={items}
						value={value}
						onValueChange={(newValue) => {
							setValue(newValue);
							setShowError(false);
						}}
					>
						<Select.Trigger/>
						<Select.Items/>
					</Select.Root>
					<Field.Error match={showError}>
						{messages["changePassword.failure.disconnectDevices"]}
					</Field.Error>
				</Field.Root>
			</Modal.Body>
			<Modal.Footer>
				<Button variant="destructive" onClick={confirm}>
					<FA6Icon icon="link-slash" variant="regular" fixedWidth className="mr-5"/>
					{messages["user.settings.connections.disconnect"]}
				</Button>
				{/* staying connected is the safe outcome, so it is the emphasised button */}
				<Modal.Close render={<Button type="button"/>}>
					<FA6Icon icon="link" variant="regular" fixedWidth className="mr-5"/>
					{messages.withArguments("user.settings.connections.stayConnected", providerName)}
				</Modal.Close>
			</Modal.Footer>
		</Modal.Content>
	);
}
